import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import ProtectedCaseStudy from '../component/ProtectedCaseStudy';

const FempaqCaseStudy = () => {
  return (
    <ProtectedCaseStudy pageName="fempaq" correctPassword="DJdesign1!">
    <div className="min-h-screen bg-white">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-200 py-4 px-4 md:px-20">
        <Link to="/" className="inline-flex items-center gap-2 text-purple-600 hover:text-pink-500 transition-colors">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Home</span>
        </Link>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-dj-dark to-dj-dark/95 text-white py-16 md:py-24 px-4 md:px-20">
        <div className="max-w-4xl mx-auto">
          <p className="text-primary text-xs font-semibold uppercase tracking-widest mb-4">Portfolio Case Study</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">Designing a health platform that has to serve <em className="italic">two very different users</em></h1>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">How I've shaped femPAQ's brand, product, and platform experience since day one — from the original logo and packaging in 2019 to architecting the core information architecture for an AI-powered women's health platform.</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-gray-600 pt-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Company</p>
              <p className="text-sm font-semibold">femPAQ Inc.</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Role</p>
              <p className="text-sm font-semibold">Technical Co-Founder & Acting CTO</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Timeline</p>
              <p className="text-sm font-semibold">2019 – Present</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Scope</p>
              <p className="text-sm font-semibold">Brand, Product, Platform UX</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="py-16 md:py-24 px-4 md:px-20">
        <div className="max-w-4xl mx-auto space-y-20">
          
          {/* Section 01 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">01 — Origin</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Before there was a platform, there was a brand — and I built it</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">femPAQ began in 2019 as a mission: make period products accessible and equitable for teens and young women, without stigma. I co-founded the company alongside April Roy, and from the very beginning my role was to give the mission a face — developing the brand identity and logo from scratch, designing the original product packaging, building the company's first WordPress website, and creating the printed marketing materials that carried femPAQ into its earliest trade shows.</p>
            <p className="text-gray-700 mb-8 leading-relaxed">That foundational work is why April points to me as femPAQ's technical co-founder whenever the company is asked if it has a technical lead — the relationship didn't start with a platform. It started with building the brand from nothing.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: '🎨', title: 'Brand & logo', desc: 'Originated the company concept and developed femPAQ\'s brand identity and logo — the visual foundation the company still operates from today.' },
                { icon: '📦', title: 'Retail packaging', desc: 'Designed all packaging for the femPAQ Teen Period Kit, translating brand strategy into shelf-ready consumer packaging now carried in Walmart stores nationally.' },
                { icon: '🌐', title: 'First website', desc: 'Built femPAQ\'s original WordPress site — the company\'s first public-facing digital presence.' },
                { icon: '🖨️', title: 'Trade show materials', desc: 'Designed all printed marketing materials that represented femPAQ at its earliest industry trade shows.' },
              ].map((item, idx) => (
                <div key={idx} className="bg-dj-light p-6 rounded-lg border-t-4 border-secondary">
                  <p className="text-2xl mb-2">{item.icon}</p>
                  <h4 className="font-bold text-dj-dark mb-2">{item.title}</h4>
                  <p className="text-gray-700 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Section 02 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">02 — The Problem</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Translating clinical complexity into something a user will actually open every day</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">As femPAQ evolved from a retail product company into femPAQ Health — an AI-powered women's health platform — the design challenge became fundamentally harder. The platform needed to cover real clinical territory: cycle tracking, contraceptive guidance, pregnancy and fertility support, perimenopause, even preeclampsia risk monitoring for a grant-funded research track. And it needed to make all of that feel approachable rather than clinical, for an audience that includes teens and young women who are often encountering this information for the first time.</p>
            
            <div className="bg-purple-100 border-l-4 border-purple-500 p-6 rounded">
              <p className="text-purple-900 font-semibold">The hardest constraint wasn't the clinical content itself — it was designing an experience credible enough to handle real health information while still feeling like something a young user would actually want to open, not a medical form she's required to fill out.</p>
            </div>
          </section>

          {/* Section 03 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">03 — Information Architecture</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">Designing the core platform IA</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">The work I'm most hands-on with is the platform's core information architecture — navigation, onboarding, and structure for the primary health experience. This meant designing a system that could hold cycle tracking, symptom logging, AI-assisted guidance, and pre-visit reporting without becoming a maze of disconnected features.</p>

            <div className="space-y-4">
              {[
                { step: '01', phase: 'Onboarding', color: 'bg-teal-100', desc: 'Designed the onboarding flow around a short health assessment, using the user\'s answers to personalize the experience from the very first session.' },
                { step: '02', phase: 'Home Experience', color: 'bg-teal-100', desc: 'Designed the home screen architecture around the user\'s current cycle phase — surfacing the most relevant tools based on context.' },
                { step: '03', phase: 'Feature Navigation', color: 'bg-purple-100', desc: 'Organized a broad set of health modules into a navigation structure a user could actually hold in her head.' },
                { step: '04', phase: 'Pre-Visit Report Flow', color: 'bg-yellow-100', desc: 'Designed the pre-visit report builder\'s structure — how tracked symptoms get organized and presented to providers.' },
              ].map((item) => (
                <div key={item.step} className={`flex gap-4 ${item.color} p-4 rounded-lg`}>
                  <div className="font-bold text-lg text-dj-dark min-w-12">{item.step}</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-dj-dark">{item.phase}</h4>
                    <p className="text-sm text-gray-700">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section 04 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">04 — Visual Direction</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">"Glossier meets women's health"</h2>
            <p className="text-gray-700 mb-8 leading-relaxed">The visual direction I set for femPAQ Health was deliberately not clinical. Health platforms tend to default to sterile blues and whites — I pushed for a direction closer to a modern beauty brand: warm, confident, and approachable, while still feeling credible enough to handle real health information.</p>

            <div className="flex flex-wrap gap-4 mb-8">
              {[
                { name: 'Teal', hex: '#1AA89D' },
                { name: 'Purple', hex: '#B773B0' },
                { name: 'Deep Purple', hex: '#5533BB' },
                { name: 'Charcoal', hex: '#555655' },
                { name: 'Lavender', hex: '#DAC5D7' },
              ].map((color) => (
                <div key={color.hex} className="text-center">
                  <div 
                    className="w-16 h-16 rounded-lg mb-2 border border-gray-300"
                    style={{ backgroundColor: color.hex }}
                  ></div>
                  <p className="text-xs font-semibold text-dj-dark">{color.name}</p>
                  <p className="text-xs text-gray-600">{color.hex}</p>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-bold text-dj-dark mb-4">What the aesthetic had to do</h3>
            <p className="text-gray-700 leading-relaxed">The diagonal teal-to-purple gradient, hexagon texture, and frosted-glass UI elements weren't decoration — they were doing real work. The direction had to make cycle tracking feel less like a medical form and more like something a user would actually want to open every day. Typography (Urbanist ExtraBold headers, clean body text) reinforces the same balance: confident without being cold.</p>
          </section>

          {/* Section 05 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">05 — Tradeoffs & Decisions</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">What I chose not to do — and why</h2>

            <div className="bg-dj-light p-6 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-dj-dark text-white">
                    <th className="p-3 text-left font-semibold">Decision</th>
                    <th className="p-3 text-left font-semibold">What I chose</th>
                    <th className="p-3 text-left font-semibold">What I didn't choose</th>
                    <th className="p-3 text-left font-semibold">Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-300">
                    <td className="p-3 font-semibold bg-white">Visual direction</td>
                    <td className="p-3 bg-white">Beauty-brand aesthetic</td>
                    <td className="p-3 bg-white">Traditional medical UI</td>
                    <td className="p-3 bg-white">Clinical UI signals institution; audience needed ownership, not treatment</td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="p-3 font-semibold bg-dj-light">Home screen</td>
                    <td className="p-3 bg-dj-light">Phase-aware dynamic content</td>
                    <td className="p-3 bg-dj-light">Static menu/dashboard</td>
                    <td className="p-3 bg-dj-light">A cycle-tracking app should feel contextual; user needs change daily</td>
                  </tr>
                  <tr className="border-b border-gray-300">
                    <td className="p-3 font-semibold bg-white">Onboarding</td>
                    <td className="p-3 bg-white">Assessment-driven personalization</td>
                    <td className="p-3 bg-white">Generic dashboard</td>
                    <td className="p-3 bg-white">Personalization upfront demonstrates value immediately</td>
                  </tr>
                  <tr>
                    <td className="p-3 font-semibold bg-dj-light">Feature organization</td>
                    <td className="p-3 bg-dj-light">Modules by user context</td>
                    <td className="p-3 bg-dj-light">Flat feature list</td>
                    <td className="p-3 bg-dj-light">Grouping by context matches how users think about health</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 06 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">06 — Outcomes</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-8">What the design decisions enabled</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                { stat: '6', label: 'years of continuous brand and product ownership' },
                { stat: '1', label: 'unified brand system spanning retail through the AI platform' },
              ].map((item, idx) => (
                <div key={idx} className="bg-gradient-to-br from-purple-100 to-pink-100 p-6 rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600 mb-2">{item.stat}</p>
                  <p className="text-sm text-gray-700">{item.label}</p>
                </div>
              ))}
            </div>

            <ul className="space-y-4">
              {[
                'Phase-aware home screen architecture in production — surfacing relevant tools based on cycle context',
                'Brand identity established in 2019 still anchors the company\'s visual language across retail and the AI platform',
                'Visual direction successfully differentiates femPAQ from clinical competitors — a beauty-brand-inspired aesthetic applied to a genuine health platform',
                'Core platform IA organizes clinical modules into a navigable structure rather than a flat feature list',
              ].map((outcome, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="text-secondary font-bold text-lg mt-1">→</span>
                  <p className="text-gray-700">{outcome}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 07 */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">07 — Reflection</p>
            <h2 className="text-3xl md:text-4xl font-bold text-dj-dark mb-6">What this project taught me about designing for trust</h2>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded mb-8">
              <h3 className="font-bold text-dj-dark mb-3">The real design problem was trust, not features</h3>
              <p className="text-gray-700">Every feature on this platform — cycle tracking, AI chat, pre-visit reporting — is in service of one underlying design problem: getting a user to trust a health platform enough to be honest with it. The clinical accuracy matters, but the interface is what determines whether someone opens the app again tomorrow or never comes back. That's the lens I applied to every IA and visual decision.</p>
            </div>

            <h3 className="text-xl font-bold text-dj-dark mb-3">What I would do differently</h3>
            <p className="text-gray-700 mb-6">I would push for more structured user testing earlier in the process. Much of the platform's IA was informed by best judgment, stakeholder input, and iteration rather than direct usability testing with the target audience — a constraint of working within a lean founding team.</p>

            <h3 className="text-xl font-bold text-dj-dark mb-3">What I'd carry into future platform work</h3>
            <p className="text-gray-700">Organize around user context, not feature inventory. A wide set of clinical capabilities can overwhelm a user if presented as a flat list — grouping by what's relevant to where the user actually is in her cycle and life stage is what makes a complex health platform feel simple.</p>
          </section>

        </div>
      </div>

      {/* Footer CTA */}
      <div className="bg-white border-t border-gray-200 text-gray-900 text-center py-12 px-4">
        <p className="mb-4"><strong>Derek Johnston</strong> — Digital Product Strategist & UX Leader</p>
        <p className="text-sm text-gray-600">dj@derekjohnston.com · (312) 608-4894</p>
      </div>
    </div>
    </ProtectedCaseStudy>
  );
};

export default FempaqCaseStudy;
